/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import { message } from '@brokerbay/design-components';
import styled from 'styled-components';
import { isEqual, reduce } from 'lodash';
import RetsListing from '@bb/domain/dist/types/RetsListing';
import RetsOpenHouse from '@bb/domain/dist/types/RetsOpenHouse';

import { Button, Collapse, Input } from 'components';

export const getRowCount = (object: Record<string, any>, minRowCount = 3) => Math.max(Math.min(Object.keys(object).length + 2, 12), minRowCount);

export const buildElement = ({
  key,
  value,
  reactKey,
  setUpdates,
}: {
  key: string;
  value: any;
  reactKey: string;
  setUpdates: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}): React.ReactNode => {
  if (typeof value === 'undefined') return null;
  if (typeof value !== 'object') {
    return (
      <Input
        key={reactKey}
        label={key}
        value={value}
        onChange={e => {
          const targetValue = e.target.value;
          setUpdates(prev => ({ ...prev, [key]: targetValue }));
        }}
      />
    );
  }

  if (!Array.isArray(value)) {
    return (
      <StyledCollapse defaultActiveKey={[]} key={reactKey}>
        <Collapse.Panel header={key} key="1">
          <StyledTextarea
            key={reactKey}
            rows={getRowCount(value)}
            defaultValue={JSON.stringify(value, null, 2)}
            onBlur={e => {
              try {
                const object = JSON.parse(e.target.value);
                setUpdates(prev => ({ ...prev, [key]: object }));
              } catch (error) {
                message.error('Invalid JSON. Reverting value.');
                e.currentTarget.value = e.currentTarget.defaultValue;
              }
            }}
          />
        </Collapse.Panel>
      </StyledCollapse>
    );
  }

  return (
    <StyledCollapse defaultActiveKey={[]} key={reactKey}>
      <Collapse.Panel header={key} key="1">
        {value.map((v, index) => (
          <StyledArrayElementContainer key={`${reactKey}.${index}`}>
            {buildElement({
              key: `${key}.${index}`,
              value: v,
              setUpdates: (
                fn: (prev: Record<string, any>) => Record<string, any>,
              ) => {
                const result = fn({})[`${key}.${index}`];
                setUpdates(prev => ({
                  ...prev,
                  [key]: value
                    .slice(0, index)
                    .concat([result])
                    .concat(value.slice(index + 1)),
                }));
              },
              // Array length is included in the key because otherwise uncontrolled Collapses and textareas
              // may not re-render after array slicing
              reactKey: `${key}${value.length}.${index}`,
            })}
            <StyledMinusButton
              onClick={() =>
                setUpdates(prev => ({
                  ...prev,
                  [key]: value.slice(0, index).concat(value.slice(index + 1)),
                }))
              }
            >
              -
            </StyledMinusButton>
          </StyledArrayElementContainer>
        ))}
        <Button
          block
          onClick={() =>
            setUpdates(prev => ({
              ...prev,
              // {} can safely be used as a default value since
              // the array root props of a retslisting always contain an array of objects
              [key]: [...value, value[0] || {}],
            }))
          }
        >
          +
        </Button>
      </Collapse.Panel>
    </StyledCollapse>
  );
};

/** Returns object difference between `obj1` and `obj2` if all the keys in the latter are in the former, otherwise returns { diff: {}, isMissingKeys: true }  */
const getDiff = (
  obj1: Record<string, any>,
  obj2: Record<string, any>,
): { diff: Record<string, any>; isMissingKeys: boolean } => {
  const missingKeysObject = { diff: {}, isMissingKeys: true };

  if (Object.keys(obj1).length !== Object.keys(obj2).length)
    return missingKeysObject;

  const diff = reduce(
    obj1,
    (result, currValue, currKey) => {
      if (!(currKey in obj2)) return missingKeysObject;
      if (isEqual(currValue, obj2[currKey])) {
        return result;
      }
      result[currKey] = currValue;
      return result;
    },
    {} as Record<string, any>,
  );
  return { diff, isMissingKeys: false };
};

export const getMongoUpdatesDiff = (
  updates: Record<string, any>,
  retsDoc: RetsListing | RetsOpenHouse,
) => {
  const transformedUpdates = { ...updates };
  Object.entries(updates).forEach(([key, value]) => {
    if (typeof value === 'object') {
      if (!Array.isArray(value)) {
        const originalValue = (retsDoc as any)[key];
        const { isMissingKeys, diff } = getDiff(value, originalValue);
        if (!isMissingKeys) {
          delete transformedUpdates[key];
          const diffKeys = Object.keys(diff);
          diffKeys.forEach(diffKey => {
            transformedUpdates[`${key}.${diffKey}`] = diff[diffKey];
          });
        }
      } else {
        const originalValue = (retsDoc as any)[key];
        if (value.length >= originalValue.length) {
          value.forEach((elementValue, index) => {
            if (index + 1 > originalValue.length) {
              transformedUpdates[`${key}.${index}`] = elementValue;
            } else if (!isEqual(elementValue, originalValue[index])) {
              const { isMissingKeys, diff } = getDiff(
                elementValue,
                originalValue[index],
              );
              if (!isMissingKeys) {
                const diffKeys = Object.keys(diff);
                diffKeys.forEach(diffKey => {
                  transformedUpdates[`${key}.${index}.${diffKey}`] =
                    diff[diffKey];
                });
              } else {
                transformedUpdates[`${key}.${index}`] = elementValue;
              }
            }
          });
          delete transformedUpdates[key];
        }
      }
    }
  });
  return transformedUpdates;
};

const StyledTextarea = styled.textarea`
  width: 100%;
`;

const StyledCollapse = styled(Collapse)`
  margin: 10px 0;
`;

const StyledArrayElementContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
`;

const StyledMinusButton = styled(Button)`
  margin-top: 10px;
`;
